import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone, ArrowRight, ArrowUpRight, Wrench, Laptop, ShoppingBag, RefreshCcw, Search, Camera, Droplets, Smartphone } from "lucide-react";
import { SITE } from "../data/site";
import { useLang } from "../i18n";
import { MaskLines, Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SectionHead } from "../components/SectionHead";
import { ImageFrame } from "../components/ImageFrame";
import { PrimaryCTA, GhostCTA } from "../components/CTA";
import { OpenStatus } from "../components/OpenStatus";
import { HeroFigure } from "../components/HeroFigure";
import { TDInline } from "../components/TDBadge";
import { DeviceDrift } from "../components/DeviceDrift";
import { FloatingIcons } from "../components/FloatingIcons";
import { Magnetic, Parallax, SlideIn, LiquidReveal } from "../components/Motion";
import { TechRail } from "../components/TechRail";
import { LiveIcon } from "../components/LiveIcon";
import { TechVideo } from "../components/TechVideo";
import { YTVideoPanel } from "../components/YTVideoPanel";
import { PhraseFloat } from "../components/Motion";
import { LiminalSection, HudFrame } from "../components/Liminal";
import { BrandMarquee } from "../components/BrandMarquee";

const EASE = [0.22, 1, 0.36, 1];

const PROCESS_IMGS = [
  { img: "https://images.unsplash.com/photo-1588515603140-81bd9f7d1db0?auto=format&fit=crop&w=1200&q=80", alt: "Phone being carefully disassembled on a repair bench" },
  { img: "https://images.unsplash.com/photo-1640955785023-1854685dae05?auto=format&fit=crop&w=1200&q=80", alt: "Close-up of precision repair work on a circuit board" },
  { img: "https://images.unsplash.com/photo-1617839625591-e5a789593135?auto=format&fit=crop&w=1200&q=80", alt: "Golden circuit board detail of a device under repair" },
  { img: "https://images.unsplash.com/photo-1611396000732-f8c9a933424f?auto=format&fit=crop&w=1200&q=80", alt: "Repaired device on a clean technician workspace" },
];

const HIGHLIGHT_META = [
  { icon: Wrench, to: "/repair-lab", id: "highlight-phone", color: "group-hover:border-volt group-hover:bg-volt" },
  { icon: Laptop, to: "/repair-lab", id: "highlight-laptop", color: "group-hover:border-eblue group-hover:bg-eblue" },
  { icon: RefreshCcw, to: "/repair-lab", id: "highlight-software", color: "group-hover:border-cyan group-hover:bg-cyan" },
  { icon: Search, to: "/repair-lab", id: "highlight-diagnostics", color: "group-hover:border-magenta group-hover:bg-magenta" },
  { icon: ShoppingBag, to: "/showroom", id: "highlight-accessories", color: "group-hover:border-accent group-hover:bg-accent" },
];

const Hero = () => {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress: hp } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // unseen-style scroll exit: copy lifts and fades, devices sink slower (depth),
  // the giant outline word drifts sideways.
  const contentY = useTransform(hp, [0, 1], [0, -110]);
  const contentOpacity = useTransform(hp, [0, 0.72], [1, 0]);
  const figureY = useTransform(hp, [0, 1], [0, 150]);
  const wordX = useTransform(hp, [0, 1], ["0%", "-16%"]);
  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <motion.span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        style={reduce ? undefined : { x: wordX }}
      >
        <span
          className="select-none whitespace-nowrap font-display text-[24vw] uppercase leading-none text-transparent"
          style={{ WebkitTextStroke: "1.5px rgba(244,244,239,0.09)", filter: "drop-shadow(0 0 34px rgba(233,180,76,0.12))" }}
        >
          Next-G
        </span>
      </motion.span>

      <span
        aria-hidden="true"
        className="absolute left-5 top-1/2 hidden -translate-y-1/2 font-mono text-[10px] uppercase tracking-[0.4em] text-ink/35 lg:block"
        style={{ writingMode: "vertical-rl" }}
      >
        Next-G · 17th Ave SE · Calgary
      </span>

      <motion.div
        className="absolute inset-0 will-change-transform"
        style={reduce ? undefined : { y: figureY }}
        aria-hidden="true"
      >
        <HeroFigure />
        <DeviceDrift device="airpods" className="left-[2%] bottom-[16%]" imgClassName="h-[60px] sm:h-[90px] lg:h-[120px]" dur={11} opacity="opacity-[0.15] lg:opacity-[0.22]" />
        <DeviceDrift device="watch-ultra" className="right-[24%] top-[9%] lg:right-[26%] lg:top-[10%]" imgClassName="h-[56px] sm:h-[84px] lg:h-[110px]" dur={13} delay={0.8} spin opacity="opacity-[0.18] lg:opacity-[0.28]" />
        <DeviceDrift device="headphones" className="left-[12%] top-[6%] lg:left-[16%] lg:top-[8%]" imgClassName="h-[52px] sm:h-[76px] lg:h-[100px]" dur={14} delay={1.4} opacity="opacity-[0.12] lg:opacity-[0.16]" />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-28 sm:px-8 lg:px-16 will-change-transform"
        style={reduce ? undefined : { y: contentY, opacity: contentOpacity }}
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-wrap items-center gap-2"
        >
          <Link
            to="/phones"
            data-testid="hero-buy-repair-badge"
            className="rounded-full bg-volt px-4 py-1.5 font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#0B111B] transition-colors duration-300 hover:bg-accent hover:text-[#EDF1F7]"
          >
            {t("hero.badge")}
          </Link>
          {SITE.languages.map((l) => (
            <span
              key={l.code}
              data-testid={`hero-lang-${l.code.toLowerCase()}`}
              className={`rounded-full border border-ink/15 bg-white/5 px-3.5 py-1.5 text-xs sm:text-sm text-ink/75 backdrop-blur-sm ${
                l.ethiopic ? "font-ethiopic" : "font-medium"
              }`}
            >
              {l.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-2 rounded-full border border-volt/25 bg-volt/5 px-3 py-1" data-testid="hero-live-chip">
            <LiveIcon variant="eq" color="#E9B44C" className="!h-6 !w-6 !rounded-lg" />
            <LiveIcon variant="ripple" color="#4FB3BF" className="!h-6 !w-6 !rounded-lg" />
          </span>
        </motion.div>

        <h1 className="depth-3d mt-8 font-display text-[13vw] sm:text-7xl lg:text-8xl uppercase leading-[0.98] tracking-tight">
          <MaskLines
            lines={[
              t("hero.h1a"),
              <span key="l2" className="text-volt font-serif-accent italic normal-case tracking-normal">{t("hero.h1b")}</span>,
              <>
                <span key="l3a" className="text-accent-bright">{t("hero.h1c").split(" ")[0]}</span>{" "}
                <span key="l3b" className="text-cyan">{t("hero.h1c").split(" ").slice(1).join(" ") || "\u00A0"}</span>
              </>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ink/60"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <Magnetic><PrimaryCTA to="/quote" testid="hero-quote-cta" dark>{t("hero.ctaQuote")}</PrimaryCTA></Magnetic>
          <Magnetic><GhostCTA href={SITE.phoneHref} testid="hero-call-cta" dark>
            {t("nav.call")} {SITE.phoneDisplay}
          </GhostCTA></Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink/55"
        >
          <a
            href={SITE.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-location-link"
            className="link-underline inline-flex items-center gap-2 hover:text-ink"
          >
            <MapPin className="h-4 w-4 text-magenta" />
            {SITE.addressShort}, {SITE.city} · {SITE.locationCue}
          </a>
          <a
            href={SITE.phoneHref}
            data-testid="hero-phone-link"
            className="link-underline inline-flex items-center gap-2 font-mono text-xs tracking-widest hover:text-ink"
          >
            <Phone className="h-4 w-4 text-volt" />
            {SITE.phoneIntl}
          </a>
        </motion.div>
      </motion.div>

      <TechRail
        testid="home-tech-rail"
        className="absolute right-5 top-1/2 hidden h-[46vh] w-64 -translate-y-1/2 xl:block"
        items={t("highlights.items").map((s) => ({ label: s.title }))}
      />
      <PhraseFloat text={t("hero.ctaVisit")} tone="gold" className="left-[3%] top-[64%] xl:top-[32%]" dur={10} />
      <PhraseFloat text={t("location.titleEm")} tone="cyan" className="right-[5%] bottom-[14%] xl:right-[18%] xl:bottom-[24%]" dur={12} delay={1.2} />

      <div className="absolute inset-x-0 bottom-0 z-10">
        <div className="mx-auto h-[3px] max-w-6xl bg-gradient-to-r from-volt via-accent to-magenta" />
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-8 gap-y-2 px-4 py-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">
            {SITE.motto}
          </span>
          <OpenStatus />
        </div>
      </div>
    </section>
  );
};

const ProcessChapter = ({ step, meta, flip }) => (
  <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14">
    <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
      <SlideIn from={flip ? "right" : "left"}>
        <div className="flex items-baseline gap-5">
          <span className="font-display text-6xl sm:text-7xl text-transparent select-none" aria-hidden="true" style={{ WebkitTextStroke: "1.5px rgba(244,244,239,0.25)" }}>
            {step.num}
          </span>
          <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{step.title}</h3>
        </div>
        <p className="mt-5 max-w-md text-base leading-relaxed text-ink/65 lg:ps-24">{step.body}</p>
      </SlideIn>
    </div>
    <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
      <SlideIn from={flip ? "left" : "right"} delay={0.08}>
        <ImageFrame src={meta.img} alt={meta.alt} ratio="aspect-[16/10]" parallax={16} />
      </SlideIn>
    </div>
  </div>
);

const Highlights = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const items = t("highlights.items");
  const previews = [
    { src: "/phones/iphone.png", glow: "rgba(46,107,230,0.4)", h: "h-[290px]" },
    { src: "/devices/laptop.png", glow: "rgba(61,111,224,0.4)", h: "h-[220px]" },
    { src: "/phones/pixel.png", glow: "rgba(127,224,195,0.4)", h: "h-[280px]" },
    { src: "/devices/broken-phone.png", glow: "rgba(46,107,230,0.4)", h: "h-[260px]" },
    { src: "/devices/airpods.png", glow: "rgba(233,180,76,0.35)", h: "h-[230px]" },
  ];
  return (
    <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
      <div className="lg:col-span-7">
        {items.map((h, i) => {
          const meta = HIGHLIGHT_META[i];
          const Icon = meta.icon;
          return (
            <Reveal key={meta.id} delay={i * 0.05}>
              <Link
                to={meta.to}
                data-testid={meta.id}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                className="group flex items-start gap-5 border-t border-sand py-6 transition-colors last:border-b hover:bg-white/5 sm:items-center"
              >
                <span className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-300 group-hover:text-[#0B111B] sm:mt-0 ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex-1">
                  <span className="font-display text-xl sm:text-2xl uppercase tracking-tight transition-colors group-hover:text-volt">
                    {h.title}
                  </span>
                  <span className="mt-1 block text-sm text-ink/60">{h.body}</span>
                </span>
                <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-ink/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent-bright sm:mt-0 rtl:-scale-x-100" />
              </Link>
            </Reveal>
          );
        })}
      </div>
      <div className="relative hidden lg:col-span-5 lg:block">
        <div className="glass-liquid sticky top-28 overflow-hidden rounded-[18px]">
          <div className="relative flex aspect-[4/5] w-full items-center justify-center">
            {previews.map((p, i) => (
              <motion.div
                key={p.src}
                className="absolute inset-0 flex items-center justify-center"
                initial={false}
                animate={{
                  opacity: active === i ? 1 : 0,
                  scale: active === i ? 1 : 0.86,
                  rotate: active === i ? 0 : -6,
                }}
                transition={{ duration: 0.55, ease: EASE }}
              >
                <motion.img
                  src={p.src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className={`w-auto ${p.h}`}
                  style={{ filter: `drop-shadow(0 0 44px ${p.glow})` }}
                  animate={active === i ? { y: [0, -14, 0] } : { y: 0 }}
                  transition={active === i ? { duration: 6, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const { t } = useLang();
  const steps = t("process.steps");
  return (
    <>
      <Hero />
      <Marquee items={t("marquee")} />

      <LiminalSection env="cyber" testid="process-section" calm className="border-y border-sand/60">
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16 lg:py-32">
        <FloatingIcons
          items={[
            { Icon: Smartphone, x: "88%", y: "6%", dur: 10, opacity: "text-eblue/20" },
            { Icon: Camera, x: "4%", y: "42%", dur: 12, delay: 1, opacity: "text-magenta/20" },
            { Icon: Droplets, x: "92%", y: "66%", dur: 9, delay: 0.5, opacity: "text-cyan/20" },
          ]}
        />
        <SectionHead
          chapter="01"
          eyebrow={t("process.eyebrow")}
          title={t("process.title")}
          lede={t("process.lede")}
        />
        <div className="mt-16 space-y-20 sm:space-y-28">
          {steps.map((p, i) => (
            <ProcessChapter key={p.num} step={p} meta={PROCESS_IMGS[i]} flip={i % 2 === 1} />
          ))}
        </div>
        </div>
      </LiminalSection>
      <BrandMarquee />

      <section data-testid="services-highlight-section" className="relative border-y border-sand bg-cream">
        <FloatingIcons
          items={[
            { Icon: Wrench, x: "90%", y: "12%", dur: 11, opacity: "text-volt/20" },
            { Icon: RefreshCcw, x: "3%", y: "70%", dur: 13, delay: 1.4, opacity: "text-accent-bright/25" },
          ]}
        />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <SectionHead
            chapter="02"
            eyebrow={t("highlights.eyebrow")}
            title={t("highlights.title")}
          />
          <Highlights />
        </div>
      </section>

      <section data-testid="buy-strip" className="border-b border-sand bg-cream">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-5 px-4 py-10 sm:px-8 lg:px-16">
          <Reveal>
            <p className="max-w-2xl font-display text-2xl sm:text-3xl uppercase leading-tight tracking-tight">
              <span className="text-volt">{t("buy.titleA")} {t("buy.titleEm")}</span>{" "}
              <span className="text-ink/60">{t("buy.titleB")}</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/phones"
              data-testid="buy-strip-link"
              className="group inline-flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 text-sm sm:text-base font-bold uppercase tracking-wide text-[#0B111B] transition-colors duration-300 hover:bg-accent hover:text-[#EDF1F7]"
            >
              {t("buy.shopLink")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* owner-picked ambient clips in polygon frames */}
      <section data-testid="bench-cam-section" className="relative border-b border-sand bg-cream">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 sm:grid-cols-2 sm:px-8 lg:px-16">
          <SlideIn from="left">
            <YTVideoPanel videoId="oCQ_WJ08fhY" label="Ambient tech clip one" testid="home-video-ink" className="aspect-[4/5] sm:aspect-[4/3]" />
          </SlideIn>
          <SlideIn from="right" delay={0.1}>
            <YTVideoPanel videoId="-d4doMas0wk" label="Ambient tech clip two" testid="home-video-bubbles" className="aspect-[4/5] sm:aspect-[4/3]" />
          </SlideIn>
        </div>
      </section>

      <section data-testid="quote-teaser-section" className="grain relative overflow-hidden border-y border-ink/10 bg-espresso text-ink">
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-volt/15 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -top-32 -right-32 h-[26rem] w-[26rem] rounded-full bg-magenta/15 blur-3xl" aria-hidden="true" />
        <DeviceDrift device="speaker" className="right-[4%] bottom-[6%]" imgClassName="h-[64px] lg:h-[130px]" dur={12} opacity="opacity-[0.2] lg:opacity-[0.3]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-12 lg:px-16">
          <LiquidReveal className="lg:col-span-7">
            <p className="eyebrow !text-volt">03 — {t("teaser.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[1.05] tracking-tight">
              {t("teaser.titleA")} <span className="text-accent-bright">{t("teaser.titleEm")}</span> {t("teaser.titleB")}
            </h2>
            <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-ink/65">
              {t("teaser.body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Magnetic><PrimaryCTA to="/quote" testid="teaser-quote-cta" dark>{t("teaser.ctaStart")}</PrimaryCTA></Magnetic>
              <Magnetic><GhostCTA href={SITE.phoneHref} testid="teaser-call-cta" dark>{t("teaser.ctaCall")} {SITE.phoneDisplay}</GhostCTA></Magnetic>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-2.5" data-testid="teaser-price-chips">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">
                {t("teaser.pricesLabel")}
              </span>
              {t("teaser.prices").map((p) => (
                <span
                  key={p}
                  className="rounded-full border border-volt/35 bg-volt/[0.08] px-4 py-1.5 font-mono text-xs sm:text-sm text-volt"
                >
                  {p}
                </span>
              ))}
            </div>
          </LiquidReveal>
          <Parallax speed={0.18} className="lg:col-span-5">
            <Stagger className="space-y-4">
              {t("teaser.steps").map((s, i) => (
                <StaggerItem key={s}>
                  <div className="flex items-center gap-5 rounded-2xl border border-ink/12 bg-ink/5 px-6 py-5">
                    <span className={`font-mono text-sm ${["text-volt", "text-accent-bright", "text-cyan"][i % 3]}`}>0{i + 1}</span>
                    <span className="font-display text-xl sm:text-2xl uppercase tracking-tight">{s}</span>
                    <ArrowRight className="ms-auto h-5 w-5 text-gold-soft rtl:-scale-x-100" />
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </Parallax>
        </div>
      </section>

      <section data-testid="location-strip-section" className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <DeviceDrift device="saturn" className="left-[1%] top-[4%]" imgClassName="h-[70px] lg:h-[140px]" dur={16} opacity="opacity-60 lg:opacity-80" />
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHead
              chapter="04"
              eyebrow={t("location.eyebrow")}
              title={<>{SITE.addressShort}, {SITE.city} <span className="text-magenta">{t("location.titleEm")}</span> <TDInline /></>}
              lede={t("location.lede")}
            />
            <div className="mt-8 space-y-4">
              <OpenStatus />
              <ul className="space-y-2 text-sm text-ink/70">
                {SITE.hours.map((h) => (
                  <li key={h.days} className="flex items-center justify-between max-w-xs border-b border-sand pb-2">
                    <span>{h.days}</span>
                    <span className="font-mono text-xs">{h.time}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 pt-2">
                <GhostCTA href={SITE.mapsUrl} testid="home-map-cta">{t("location.openMaps")}</GhostCTA>
                <GhostCTA to="/visit" testid="home-visit-cta">{t("location.visitContact")}</GhostCTA>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <ImageFrame
                src="https://images.unsplash.com/photo-1785474445369-409f6acec42b?auto=format&fit=crop&w=1400&q=80"
                alt="Clean repair studio desk with laptop and tools ready for the next customer"
                ratio="aspect-[16/11]"
                parallax={30}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* liminal interlude — post-apocalyptic forest ruins you drift through */}
      <LiminalSection env="ruins" testid="ruins-interlude" strength={0.22} className="border-y border-sand/60">
        <div className="relative mx-auto flex h-[62vh] max-w-7xl items-center justify-center px-4 sm:px-8 lg:px-16">
          <span
            aria-hidden="true"
            className="select-none whitespace-nowrap font-display text-[16vw] uppercase leading-none text-transparent"
            style={{ WebkitTextStroke: "1.5px rgba(244,244,239,0.16)", filter: "drop-shadow(0 0 30px rgba(233,180,76,0.14))" }}
          >
            Next-G
          </span>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "140px" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-10 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/55"
          >
            {SITE.motto}
          </motion.p>
        </div>
      </LiminalSection>
    </>
  );
}
